/**
 * Real Evolu data source for NWC connection persistence.
 *
 * Implements NwcConnectionsDataSource from useNwcConnections, reading
 * from and writing to the walletConnection table. Handles Evolu branded
 * type conversions using getOrThrow(Type.from(x)) pattern.
 *
 * SECURITY: connectionSecret is stored encrypted at rest by Evolu.
 * On removal, connectionSecret/pubkey/relayUrl are nulled out before
 * soft-deleting (LN-11). Secrets are never logged or exposed.
 *
 * Tier: 2 (Lightning features).
 *
 * @module lightning/hooks/useNwcConnectionsDataSource
 */

import { useMemo, useCallback, useRef } from 'react';
import { useEvolu, useQuery } from '@evolu/react';
import { NonEmptyString1000, SqliteBoolean, getOrThrow } from '@evolu/common';
import type { Evolu } from '@evolu/common';
import type { SatsflowSchema } from '@/db/database';
import type {
  NwcConnectionsDataSource,
  NwcConnectionRecord,
  AddConnectionData,
  UpdateConnectionData,
} from './useNwcConnections';

/**
 * Cast the generic Evolu instance to the app's typed schema.
 * Same pattern used in useBackupCompleted and useMints.
 *
 * @param evolu - Generic Evolu instance
 * @returns Typed Evolu instance
 */
function typedEvolu(evolu: ReturnType<typeof useEvolu>): Evolu<SatsflowSchema> {
  return evolu as unknown as Evolu<SatsflowSchema>;
}

/**
 * Parse a capabilities JSON string into a string array.
 *
 * @param raw - JSON string or null from Evolu
 * @returns Parsed string array or null
 */
function parseCapabilities(raw: string | null): string[] | null {
  if (raw === null) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.every((item) => typeof item === 'string')) {
      return parsed as string[];
    }
    return null;
  } catch {
    return null;
  }
}

/** Return type for useNwcConnectionsDataSource. */
export interface UseNwcConnectionsDataSourceResult {
  /** The real Evolu-backed data source for NWC connections. */
  readonly dataSource: NwcConnectionsDataSource;
}

/**
 * Provide a real Evolu data source for NWC connections.
 *
 * Queries the walletConnection table for all non-deleted rows and
 * provides CRUD operations with proper Evolu branded type conversions.
 * Removal securely wipes connectionSecret, pubkey, and relayUrl before
 * soft-deleting (LN-11 requirement).
 *
 * @returns Object with dataSource for useNwcConnections
 */
export function useNwcConnectionsDataSource(): UseNwcConnectionsDataSourceResult {
  const evolu = useEvolu();

  // Memoize the query to keep a stable reference across renders.
  // Without this, each render creates a new query object, causing useQuery
  // to unsubscribe/resubscribe. Mutations sent with the old subscription
  // would have their patches ignored by the new subscription.
  const connectionsQueryRef = useRef(
    evolu.createQuery((db) =>
      db
        .selectFrom('walletConnection')
        .select([
          'id',
          'type',
          'label',
          'connectionSecret',
          'pubkey',
          'relayUrl',
          'isActive',
          'lastConnectedAt',
          'lastKnownBalanceSats',
          'capabilities',
        ])
        .where('isDeleted', 'is not', 1 as never),
    ),
  );
  const connectionsQuery = connectionsQueryRef.current;

  const rows = useQuery(connectionsQuery);

  const connections: readonly NwcConnectionRecord[] = useMemo(
    () =>
      rows
        // Filter out phantom rows created by Evolu upsert (null label = not a real connection)
        .filter((row) => row.label != null)
        .map((row) => ({
          id: String(row.id),
          type: row.type != null ? String(row.type) : 'nwc',
          label: row.label != null ? String(row.label) : '',
          connectionSecret: String(row.connectionSecret),
          pubkey: String(row.pubkey),
          relayUrl: String(row.relayUrl),
          isActive: row.isActive === 1,
          lastConnectedAt: row.lastConnectedAt != null ? String(row.lastConnectedAt) : null,
          lastKnownBalanceSats:
            row.lastKnownBalanceSats != null ? (row.lastKnownBalanceSats as number) : null,
          capabilities: parseCapabilities(
            row.capabilities != null ? String(row.capabilities) : null,
          ),
        })),
    [rows],
  );

  const addConnection = useCallback(
    (data: AddConnectionData): void => {
      const db = typedEvolu(evolu);
      const isFirst = connections.length === 0;

      db.insert('walletConnection', {
        type: getOrThrow(NonEmptyString1000.from(data.type)),
        label: getOrThrow(NonEmptyString1000.from(data.label)),
        connectionSecret: getOrThrow(NonEmptyString1000.from(data.connectionSecret)),
        pubkey: getOrThrow(NonEmptyString1000.from(data.pubkey)),
        relayUrl: getOrThrow(NonEmptyString1000.from(data.relayUrl)),
        isActive: getOrThrow(SqliteBoolean.from(isFirst ? 1 : 0)),
        lastConnectedAt: null,
        capabilities: data.capabilities
          ? getOrThrow(NonEmptyString1000.from(JSON.stringify(data.capabilities)))
          : null,
      });
    },
    [evolu, connections.length],
  );

  const removeConnection = useCallback(
    (id: string): void => {
      const db = typedEvolu(evolu);
      // SECURITY (LN-11): Overwrite secrets with placeholder BEFORE soft-deleting.
      // connectionSecret, pubkey, and relayUrl are NonEmptyString1000 (not nullable),
      // so we overwrite with a redacted value instead of null.
      // isDeleted must be SqliteBoolean (branded), not a raw JS boolean.
      const redacted = getOrThrow(NonEmptyString1000.from('[REMOVED]'));
      db.update('walletConnection', {
        id,
        connectionSecret: redacted,
        pubkey: redacted,
        relayUrl: redacted,
        isDeleted: getOrThrow(SqliteBoolean.from(1)),
      } as never);
    },
    [evolu],
  );

  const updateConnection = useCallback(
    (id: string, data: UpdateConnectionData): void => {
      const db = typedEvolu(evolu);
      const updateData: Record<string, unknown> = { id };

      if (data.label !== undefined) {
        updateData.label = getOrThrow(NonEmptyString1000.from(data.label));
      }
      if (data.lastConnectedAt !== undefined) {
        updateData.lastConnectedAt = getOrThrow(NonEmptyString1000.from(data.lastConnectedAt));
      }
      if (data.capabilities !== undefined) {
        updateData.capabilities = getOrThrow(
          NonEmptyString1000.from(JSON.stringify(data.capabilities)),
        );
      }
      if (data.isActive !== undefined) {
        updateData.isActive = getOrThrow(SqliteBoolean.from(data.isActive ? 1 : 0));
      }
      if (data.lastKnownBalanceSats !== undefined) {
        updateData.lastKnownBalanceSats = data.lastKnownBalanceSats;
      }

      db.update('walletConnection', updateData as never);
    },
    [evolu],
  );

  const dataSource: NwcConnectionsDataSource = useMemo(
    () => ({
      connections,
      addConnection,
      removeConnection,
      updateConnection,
    }),
    [connections, addConnection, removeConnection, updateConnection],
  );

  return { dataSource };
}
